import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  AutocompleteInputChangeReason,
  AutocompleteRenderInputParams
} from '@mui/material';
import type {
  HTMLAttributes,
  KeyboardEvent,
  Key,
  MouseEvent,
  SyntheticEvent
} from 'react';

import { Autocomplete } from '@mui/material';

import { useEntityOptionColumns } from 'components/mrp/components/EntitySearch/EntitySearch.hooks';
import { EntitySearchContainer } from 'components/mrp/components/EntitySearch/EntitySearch.styles';
import { EntitySearchInput } from 'components/mrp/components/EntitySearch/components/EntitySearchInput';
import { EntitySearchOptionItem } from 'components/mrp/components/EntitySearch/components/EntitySearchOptionItem/EntitySearchOptionItem';

import OrganizationalUnitOptionItem from './components/OrganizationalUnitOptionItem';
import PlanningEntityPickerPaper from './components/PlanningEntityPickerPaper';
import PlanningEntitySelectionChips from './components/PlanningEntitySelectionChips';
import PlanningEntityTypeFilter from './components/PlanningEntityTypeFilter';
import { useOrganizationalUnitPerspectiveChoice } from './hooks/useOrganizationalUnitPerspectiveChoice';
import { useOrganizationalUnitSearchOptions } from './hooks/useOrganizationalUnitSearchOptions';
import { usePlanningEntitySearchOptions } from './hooks/usePlanningEntitySearchOptions';
import { usePlanningEntityTypeFilter } from './hooks/usePlanningEntityTypeFilter';
import { PickerContainer } from './PlanningEntityPicker.styles';
import { ORGANIZATIONAL_UNIT_PERSPECTIVES } from './PlanningEntityPicker.types';
import {
  addSelection,
  claimKey,
  getPeopleTypes,
  getSearchedTypes,
  getSelectionKey,
  isOrganizationalUnitSearchActive
} from './PlanningEntityPicker.utils';

import type { PlanningEntityPickerPaperProps } from './components/PlanningEntityPickerPaper/PlanningEntityPickerPaper.types';
import type {
  OrganizationalUnitPerspective,
  PlanningEntityPickerOption,
  PlanningEntityPickerProps
} from './PlanningEntityPicker.types';

/**
 * One field for "who": the picked people and plan units as chips, and a search that adds one more per pick.
 * A pick adds its chip straight away and empties the search for the next one.
 */
const PlanningEntityPicker = ({
  value,
  onChange,
  entityTypes,
  searchOrganizationalUnits = false,
  organizationalUnitPerspectives = ORGANIZATIONAL_UNIT_PERSPECTIVES,
  typeFilters,
  defaultTypeFilter,
  placeholder,
  minWidth = 360,
  showSelection = true
}: PlanningEntityPickerProps) => {
  'use memo';

  const { t } = useTranslation('common');
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  // The card, as the list's anchor: the list opens under the type chips rather than over them, so they stay
  // usable while it is open. State rather than a ref, since it is read during render.
  const [cardElement, setCardElement] = useState<HTMLDivElement | null>(null);

  const { showAll, types, activeFilter, selectType, applyFilterCommand } =
    usePlanningEntityTypeFilter({
      entityTypes,
      searchOrganizationalUnits,
      typeFilters,
      defaultTypeFilter
    });
  const people = usePlanningEntitySearchOptions({
    query,
    types: getPeopleTypes(activeFilter, entityTypes)
  });
  const pickedKeys = new Set(value.map(getSelectionKey));
  const organizationalUnitOptions = useOrganizationalUnitSearchOptions({
    query,
    enabled: isOrganizationalUnitSearchActive(
      activeFilter,
      searchOrganizationalUnits
    ),
    perspectives: organizationalUnitPerspectives,
    pickedKeys
  });
  const perspectiveChoice = useOrganizationalUnitPerspectiveChoice();
  const columns = useEntityOptionColumns({ mode: 'default' });

  // People before plan units: groupBy needs each group's options together. A plan unit only comes with
  // the perspectives not picked yet, so only the people need leaving out here.
  const options = [
    ...people.options.filter(
      (option) => !pickedKeys.has(getSelectionKey(option.selection))
    ),
    ...organizationalUnitOptions
  ];

  // Closing forgets the perspective chosen on a row, so the next search starts every row at its default.
  const close = () => {
    setIsOpen(false);
    perspectiveChoice.reset();
  };

  const pick = (option: PlanningEntityPickerOption) => {
    onChange(addSelection(value, option.selection));
    setQuery('');
    close();
  };

  const pickFromPerspective = (
    option: Extract<
      PlanningEntityPickerOption,
      { kind: 'ORGANIZATIONAL_UNIT' }
    >,
    perspective: OrganizationalUnitPerspective
  ) => pick({ ...option, selection: { ...option.selection, perspective } });

  // A click on the row or Enter: a plan unit from the perspective its switch stands at.
  const handleChange = (
    _event: SyntheticEvent,
    option: PlanningEntityPickerOption | null
  ) => {
    if (!option) return;
    if (option.kind === 'ORGANIZATIONAL_UNIT') {
      pickFromPerspective(option, perspectiveChoice.getPerspective(option));
    } else {
      pick(option);
    }
  };

  const handleInputChange = (
    _event: SyntheticEvent,
    nextQuery: string,
    reason: AutocompleteInputChangeReason
  ) => {
    if (reason === 'input' || reason === 'clear') {
      setQuery(nextQuery);
      perspectiveChoice.handleTyping();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (perspectiveChoice.handleArrowKey(event, options)) {
      // The perspective switch's key, not the caret's, nor the Autocomplete's own handling.
      claimKey(event);
      return;
    }
    if (applyFilterCommand(event, query)) setQuery('');
  };

  // As EntitySearch: "Zoek op " and the types the search covers now — the active chips, or all of them.
  const typePlaceholder =
    t('mrp.searchEntity') +
    getSearchedTypes(activeFilter, entityTypes, searchOrganizationalUnits)
      .map((type) => t(`mrp.entityTypeMap.${type}.plural`).toLowerCase())
      .join(', ');

  const handleReset = (event: MouseEvent<SVGSVGElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setQuery('');
  };

  // EntitySearch's own field: the magnifying glass, and ✕ to empty the search in place of the popup arrow.
  const renderInput = (params: AutocompleteRenderInputParams) => (
    <EntitySearchInput
      params={params}
      placeholder={placeholder ?? typePlaceholder}
      handleReset={handleReset}
    />
  );

  const renderOption = (
    props: HTMLAttributes<HTMLLIElement> & { key: Key },
    option: PlanningEntityPickerOption
  ) => {
    const { key, ...optionProps } = props;
    return option.kind === 'ENTITY' ? (
      <EntitySearchOptionItem
        key={key}
        props={optionProps}
        option={option.searchOption}
        columns={columns}
        isDebouncing={false}
      />
    ) : (
      <OrganizationalUnitOptionItem
        key={key}
        props={optionProps}
        selection={option.selection}
        perspectives={option.perspectives}
        activePerspective={perspectiveChoice.getPerspective(option)}
        onPickPerspective={(perspective) =>
          pickFromPerspective(option, perspective)
        }
      />
    );
  };

  // The type chips head the result list: they are only in view while the search is in use, so the field
  // takes no more room than the filters beside it.
  const paperProps: PlanningEntityPickerPaperProps = {
    header: (
      <PlanningEntityTypeFilter
        showAll={showAll}
        types={types}
        activeFilter={activeFilter}
        onSelectType={selectType}
      />
    )
  };

  return (
    <PickerContainer>
      {/* EntitySearch's card around the field, one line high like the filters beside it. First, with the
          picked chips after it, so the search stays in place however many are picked. */}
      <EntitySearchContainer ref={setCardElement} minWidth={minWidth}>
        <Autocomplete<PlanningEntityPickerOption>
          open={isOpen}
          onOpen={() => setIsOpen(true)}
          onClose={close}
          value={null}
          inputValue={query}
          options={options}
          // Filtered already: people by the backend, plan units by their own matcher.
          filterOptions={(unfiltered) => unfiltered}
          getOptionLabel={(option) => option.selection.label}
          getOptionKey={(option) => option.key}
          groupBy={(option) =>
            option.kind === 'ENTITY'
              ? t('planningEntityPicker.groups.people')
              : t('planningEntityPicker.groups.organizationalUnits')
          }
          loading={people.isLoading}
          loadingText={t('loading')}
          noOptionsText={
            query ? t('noOptions') : t('planningEntityPicker.typeToSearch')
          }
          autoHighlight
          onChange={handleChange}
          onHighlightChange={perspectiveChoice.handleHighlightChange}
          onInputChange={handleInputChange}
          onKeyDown={handleKeyDown}
          renderInput={renderInput}
          renderOption={renderOption}
          slots={{ paper: PlanningEntityPickerPaper }}
          slotProps={{
            popper: { anchorEl: cardElement },
            paper: paperProps
          }}
        />
      </EntitySearchContainer>
      {showSelection && (
        <PlanningEntitySelectionChips value={value} onChange={onChange} />
      )}
    </PickerContainer>
  );
};

export default PlanningEntityPicker;
